import React, { useState, useEffect } from 'react';
import {
  Button,
  Dropdown,
  Option,
  Spinner,
  Input,
} from '@fluentui/react-components';
import { AddRegular } from '@fluentui/react-icons';
// import { ModuleService } from '../../../../services/module.service';
// import { AuthService } from '../../../../services/auth.service';
// import { UsersService } from '../../../../services/users.service';
import { Module } from '../../../../models/module';
import { Tendering, TenderingPart, Question } from '../../../../models/tendering';
import { UserSystemData } from '../../../../models/users';
import { deepCloneObject } from '../../../../utils/clone';
import { getTenderingDocument, updateTenderingDocument, updateTenderingWithNewQuestion, generateTenderingFromExcel, getQuestionsFromExcel } from '../../../../services/tenderingService';
import { getModule } from '../../../../services/moduleService';
import authService from '../../../../services/authService';
import { createNewUsersByModule } from '../../../../services/usersService';
import NewQuestionDialog from '../SystemDialogs/NewQuestionDialog';
import CreateTenderingDialog from '../SystemDialogs/CreateTenderingDialog';
import QuestionTypesDialog from '../SystemDialogs/QuestionTypesDialog';
import TenderingCreatedDialog from '../SystemDialogs/TenderingCreatedDialog';
import CreateTenderingPartsDialog from '../SystemDialogs/CreateTenderingPartsDialog';
import CreateUsersDialog from '../SystemDialogs/CreateUsersDialog';
import ErrorDialog from '../../ErrorDialog';
// import NewQuestionDialog from './NewQuestionDialog';
// import ConfirmationDialog from '../../../shared/ConfirmationDialog';
// import PasswordDialog from '../../../shared/PasswordDialog';

interface SystemTenderingProps {
  tenderingOptions: Module[];
  user: UserSystemData;
  systemPassword: string;
}

const SystemTendering: React.FC<SystemTenderingProps> = ({
  tenderingOptions,
  user,
  systemPassword,
}) => {
  const [selectedTendering, setSelectedTendering] = useState<Module | null>(null);
  const [tendering, setTendering] = useState<Tendering | null>(null);
  const [tenderingLoaded, setTenderingLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Dialog states
  const [newQuestionDialogOpen, setNewQuestionDialogOpen] = useState(false);
  const [selectedTenderingPart, setSelectedTenderingPart] = useState<number | null>(null);
  const [createTenderingOpen, setCreateTenderingOpen] = useState(false);
  const [createUsersOpen, setCreateUsersOpen] = useState(false);
  const [createPartsOpen, setCreatePartsOpen] = useState(false);
  const [questionTypesOpen, setQuestionTypesOpen] = useState(false);
  const [tenderingCreatedOpen, setTenderingCreatedOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogData, setConfirmDialogData] = useState<any>(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [questions, setQuestions] = useState<any[]>([]);
  const [newTenderingOptions, setNewTenderingOptions] = useState<Module[]>(tenderingOptions);
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [newTenderingName, setNewTenderingName] = useState('');

  // const moduleService = new ModuleService();
  // const authService = new AuthService();
  // const userService = new UsersService();

  const tenderingProperties = ['content'];
  const questionTypes = [
    'Bifogat dokument',
    'Fritextsvar',
    'Fasta svaralternativ',
    'Linjär skala',
    'Prisruta',
    'Ja/Nej svar',
  ];

  const onTenderingSelect = async (moduleID: number) => {
    const selected = newTenderingOptions.find(t => t.moduleID === moduleID);
    setSelectedTendering(selected || null);
    setTenderingLoaded(false);
    
    try {
      const tenderingData = await getTenderingDocument(moduleID, tenderingProperties)
      setTendering(tenderingData);
      setTenderingLoaded(true);
    } catch (error) {
      console.error(error);
      setErrorDialogOpen(true);
    }
  };

  const openNewQuestionModal = (partID: number) => {
    setSelectedTenderingPart(partID);
    setNewQuestionDialogOpen(true);
  };

  const createNewQuestion = async (questionData: { heading: string; text: string }) => {
    if (!tendering || !selectedTenderingPart || !selectedTendering) return;
    
    setLoading(true);
    try {
      const newQuestion: Question = {
        heading: questionData.heading,
        text: questionData.text,
        tenderingPartID: selectedTenderingPart,
        questionType: 'Fritextsvar',
        questionID: 99000 + Math.floor(Math.random() * 100) + 1,
      };

      const part = tendering.tenderingParts.find(p => p.tenderingPartID === selectedTenderingPart);
      if (part) {
        if (!part.questions) {
          part.questions = [];
        }
        part.questions.push(newQuestion);
      }

      const { tenderingParts } = tendering;
      const requestObject = {
        content: { tenderingParts },
        questionID: newQuestion.questionID,
      };

      await updateTenderingWithNewQuestion(selectedTendering.moduleID, requestObject)
      setLoading(false);
      setNewQuestionDialogOpen(false);
    } catch (error) {
      console.error(error);
      setErrorDialogOpen(true);
      setLoading(false);
    }
  };

  const onArchiveClick = () => {
    if (!tendering) return;
    setPasswordDialogOpen(true);
  };

  const handleArchiveConfirm = async () => {
    if (!tendering || !selectedTendering) return;
    
    try {
      if (!tendering.archivingUsers) {
        tendering.archivingUsers = [user.user.userID];
        await updateTenderingDocument(tendering.tenderingID, tendering)
      } else {
        tendering.archivingUsers.push(user.user.userID);
        await updateTenderingDocument(selectedTendering.moduleID, tendering)
        
        await authService.refresh();
        const modules = user.modules;
        setTendering(null);
        setNewTenderingOptions(modules.filter(m => m.moduleType === 'Tendering'));
      }
      setPasswordDialogOpen(false);
    } catch (error) {
      console.error(error);
      setErrorDialogOpen(true);
    }
  };

  const disableButtonText = (): [boolean, string] => {
    if (!tendering?.archivingUsers) {
      return [false, 'Avpublicera ramavtal'];
    } else if (tendering.archivingUsers.length > 1) {
      return [true, 'Ramavtal avpublicerad'];
    } else if (tendering.archivingUsers.includes(user.user.userID)) {
      return [true, 'Inväntar avpublicering'];
    } else {
      return [false, 'Bekräfta avpublicering'];
    }
  };

  const openExcelModal = () => {
    setCreateTenderingOpen(true);
  };

  const handleFileUpload = async (file: File, listFile: File | null, tenderingName: string, excelType: number) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('moduleName', tenderingName);

    if (listFile) {
      formData.append('listExcel', listFile);
    }

    setExcelFile(file);
    setNewTenderingName(tenderingName);
    setLoading(true);

    try {
      if (excelType === 1) {
        const newModuleID = await generateTenderingFromExcel(formData);
        const newModule = await getModule(newModuleID);
        setNewTenderingOptions([...newTenderingOptions, newModule]);
        setCreateTenderingOpen(false);
        setTenderingCreatedOpen(true);
      } else {
        const res = await getQuestionsFromExcel(formData);
        setQuestions(res);
        setCreateTenderingOpen(false);
        setQuestionTypesOpen(true);
      }
    } catch (error) {
      console.error(error);
      setErrorDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const sendFileWithQuestions = async (questionsWithTypes: any[]) => {
    if (!excelFile) return;

    const formData = new FormData();
    formData.append('file', excelFile);
    questionsWithTypes.forEach((elem, index) => {
      formData.append(`questions[${index}]`, JSON.stringify(elem));
    });
    formData.append('moduleName', newTenderingName);

    setLoading(true);
    try {
      const newModuleID = await generateTenderingFromExcel(formData);
      const newModule = await getModule(newModuleID);
      setNewTenderingOptions([...newTenderingOptions, newModule]);
      setQuestionTypesOpen(false);
      setTenderingCreatedOpen(true);
    } catch (error) {
      console.error(error);
      setErrorDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const checkTenderingContactMappings = (): boolean => {
    if (!tendering) return false;
    return (
      tendering.properties.questionMappings?.contactEmail > 0 &&
      tendering.properties.questionMappings?.contactName > 0
    );
  };

  const createUsers = async () => {
    if (!tendering) return;

    try {
      await createNewUsersByModule(tendering.tenderingID);
      setCreateUsersOpen(false);
    } catch (error) {
      console.error(error);
      setCreateUsersOpen(false);
      setErrorDialogOpen(true);
    }
  };

  const openCreateUsersDialog = () => {
    setCreateUsersOpen(true);
    if (checkTenderingContactMappings()) {
      createUsers();
    }
  };

  const handleTenderingPartsSaved = (updatedTendering: Tendering) => {
    setTendering(updatedTendering);
    setCreatePartsOpen(false);
  };

  return (
    <div className="flex flex-col h-full">
      {user.user.userID === 1 && (
        <div className="p-4 border-b border-gray-200">
          <Button appearance="primary" icon={<AddRegular />} onClick={openExcelModal}>
            Skapa ny ramavtal
          </Button>
        </div>
      )}

      <div className="p-6 space-y-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <Dropdown
              placeholder="Välj Upphandling"
              value={selectedTendering?.moduleName || ''}
              onOptionSelect={(_, data) => onTenderingSelect(Number(data.optionValue))}
              style={{ minWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              positioning={{autoSize: false}}
              listbox={{
                style: {
                  maxHeight: '300px',
                  overflowY: 'auto'
                },
              }}
            >
              {newTenderingOptions.map((option) => (
                <Option key={option.moduleID} value={option.moduleID.toString()}>
                  {option.moduleName}
                </Option>
              ))}
            </Dropdown>
          </div>

          {tendering && tenderingLoaded && (
            <>
              {/* <Button
                appearance="primary"
                disabled={disableButtonText()[0]}
                onClick={onArchiveClick}
                className={disableButtonText()[0] ? '' : 'bg-red-600 hover:bg-red-700'}
              >
                {disableButtonText()[1]}
              </Button> */}

              {user.user.userID === 1 && (
                <Button appearance="primary" onClick={openCreateUsersDialog}>
                  Skapa admin användare för ramavtalet
                </Button>
              )}

              {user.user.userID === 1 && (
                <Button appearance="primary" onClick={() => setCreatePartsOpen(true)}>
                  Skapa nya delområde
                </Button>
              )}
            </>
          )}
        </div>

        {tendering && (
          <div className="space-y-6 mt-6">
            {tendering.tenderingParts.map((part) => (
              <div key={part.tenderingPartID} className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">{part.tenderingPart}</h3>
                <Button
                  appearance="primary"
                  onClick={() => openNewQuestionModal(part.tenderingPartID)}
                  className="mb-4"
                >
                  Skapa ny fråga
                </Button>

                <div className="space-y-2 mt-2">
                  {part.questions?.map((question) => (
                    <div
                      key={question.questionID}
                      className="flex gap-4 p-3 border border-gray-200 rounded"
                    >
                      <p className="font-mono text-sm text-gray-600">{question.questionID}</p>
                      <p className="text-sm text-gray-600">{question.questionType}</p>
                      <p className="flex-1">{question.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dialogs */}
      {newQuestionDialogOpen && (
        <NewQuestionDialog
          isOpen={newQuestionDialogOpen}
          onClose={() => setNewQuestionDialogOpen(false)}
          onSave={createNewQuestion}
          loading={loading}
        />
      )}

      {createTenderingOpen && (
        <CreateTenderingDialog
          isOpen={createTenderingOpen}
          onClose={() => setCreateTenderingOpen(false)}
          onUpload={handleFileUpload}
          loading={loading}
        />
      )}

      {questionTypesOpen && (
        <QuestionTypesDialog
          isOpen={questionTypesOpen}
          onClose={() => setQuestionTypesOpen(false)}
          questions={questions}
          questionTypes={questionTypes}
          onSend={sendFileWithQuestions}
          loading={loading}
        />
      )}

      {tenderingCreatedOpen && (
        <TenderingCreatedDialog
          isOpen={tenderingCreatedOpen}
          onClose={() => setTenderingCreatedOpen(false)}
        />
      )}

      {createUsersOpen && (
        <CreateUsersDialog
          isOpen={createUsersOpen}
          onClose={() => setCreateUsersOpen(false)}
          hasValidMappings={checkTenderingContactMappings()}
        />
      )}

      {createPartsOpen && tendering && (
        <CreateTenderingPartsDialog
          isOpen={createPartsOpen}
          onClose={() => setCreatePartsOpen(false)}
          tendering={tendering}
          onSaved={handleTenderingPartsSaved}
          onError={() => setErrorDialogOpen(true)}
        />
      )}

      {/* {passwordDialogOpen && (
        <PasswordDialog
          isOpen={passwordDialogOpen}
          onClose={() => setPasswordDialogOpen(false)}
          onConfirm={handleArchiveConfirm}
          title={`Vill du avpublicera ${tendering?.tenderingName}?`}
          user={user.user}
          presetPassword={systemPassword}
        />
      )} */}

      {/* {confirmDialogOpen && confirmDialogData && (
        <ConfirmationDialog
          isOpen={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
          onConfirm={confirmDialogData.onConfirm}
          title={confirmDialogData.title}
          text={confirmDialogData.text}
        />
      )} */}

      {errorDialogOpen && (
        <ErrorDialog isOpen={errorDialogOpen} onClose={() => setErrorDialogOpen(false)} />
      )}
    </div>
  );
};

export default SystemTendering;
